import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Upload, X, Image, User, MessageSquare, Loader2, CheckCircle2, ArrowLeft } from 'lucide-react';
import { toast } from './ui/sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const GalleryPage = () => {
  const [screenshots, setScreenshots] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [uploaderName, setUploaderName] = useState('');
  const [caption, setCaption] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [lightboxImage, setLightboxImage] = useState(null);
  const fileInputRef = useRef(null);

  const fetchApprovedScreenshots = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/screenshots/approved`);
      const data = await response.json();
      if (data.success) {
        setScreenshots(data.screenshots);
      }
    } catch (error) {
      // Silently handle fetch errors
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchApprovedScreenshots();
  }, [fetchApprovedScreenshots]);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('File size must be less than 5MB');
        return;
      }
      
      if (!file.type.startsWith('image/')) {
        toast.error('Please select an image file');
        return;
      }
      
      setSelectedImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrl(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    
    if (!selectedImage || !uploaderName) {
      toast.error('Please select an image and enter your name');
      return;
    }
    
    setIsUploading(true);
    
    try {
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64Data = reader.result;
        
        const response = await fetch(`${API_URL}/api/screenshots/upload`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            image_data: base64Data,
            filename: selectedImage.name,
            uploader_name: uploaderName,
            caption: caption
          })
        });
        
        const data = await response.json();
        
        if (data.success) {
          toast.success(data.message);
          setShowUploadModal(false);
          resetUploadForm();
        } else {
          toast.error(data.message || 'Upload failed');
        }
        
        setIsUploading(false);
      };
      reader.readAsDataURL(selectedImage);
    } catch (error) {
      toast.error('Upload failed. Please try again.');
      setIsUploading(false);
    }
  };

  const resetUploadForm = () => {
    setSelectedImage(null);
    setPreviewUrl(null);
    setUploaderName('');
    setCaption('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="min-h-screen bg-page-bg">
      {/* Header */}
      <header className="bg-card-bg border-b border-border-light sticky top-0 z-40">
        <div className="container-custom py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Link to="/" className="flex items-center gap-2 text-text-secondary hover:text-brand-primary transition-colors">
              <ArrowLeft size={20} />
              <span className="body-small">Back</span>
            </Link>
            <div className="w-px h-6 bg-border-light"></div>
            <h1 className="heading-4 text-brand-primary">Screenshot Gallery</h1>
          </div>
          <button
            onClick={() => setShowUploadModal(true)}
            className="btn-primary flex items-center gap-2"
            data-testid="upload-screenshot-btn"
          >
            <Upload size={18} />
            Upload Screenshot
          </button>
        </div>
      </header>

      {/* Gallery Content */}
      <div className="container-custom py-8">
        <div className="text-center mb-12">
          <h2 className="heading-2 text-brand-primary mb-4">Community Screenshots</h2>
          <p className="body-large text-text-secondary max-w-2xl mx-auto">
            Epic moments captured by our community. Share your own battles and showcase your elemental mastery!
          </p>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="animate-spin text-brand-primary" size={48} />
          </div>
        ) : screenshots.length === 0 ? (
          <div className="text-center py-20">
            <Image size={64} className="mx-auto mb-4 text-text-secondary opacity-50" />
            <h3 className="heading-5 text-text-secondary mb-2">No Screenshots Yet</h3>
            <p className="body-small text-text-secondary mb-6">
              Be the first to share your epic battle moments!
            </p>
            <button
              onClick={() => setShowUploadModal(true)}
              className="btn-primary"
            >
              Upload First Screenshot
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {screenshots.map((screenshot) => (
              <div
                key={screenshot.id}
                className="group bg-card-bg rounded-2xl overflow-hidden border border-border-light hover:border-brand-primary/50 transition-all cursor-pointer"
                onClick={() => setLightboxImage(screenshot)}
                data-testid={`screenshot-${screenshot.id}`}
              >
                <div className="aspect-video relative overflow-hidden">
                  <img
                    src={screenshot.image_data}
                    alt={screenshot.filename}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <div className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <User size={14} className="text-brand-primary" />
                    <span className="body-small text-text-primary font-medium">
                      {screenshot.uploader_name}
                    </span>
                  </div>
                  {screenshot.caption && (
                    <p className="body-small text-text-secondary line-clamp-2">
                      {screenshot.caption}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-card-bg rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-border-light flex justify-between items-center">
              <h3 className="heading-5 text-brand-primary">Upload Screenshot</h3>
              <button
                onClick={() => {
                  setShowUploadModal(false);
                  resetUploadForm();
                }}
                className="text-text-secondary hover:text-brand-primary transition-colors"
              >
                <X size={24} />
              </button>
            </div>
            
            <form onSubmit={handleUpload} className="p-6 space-y-6">
              {/* Image Upload Area */}
              <div>
                <label className="form-label mb-2 block">Screenshot</label>
                {previewUrl ? (
                  <div className="relative rounded-xl overflow-hidden border border-border-light">
                    <img src={previewUrl} alt="Preview" className="w-full aspect-video object-cover" />
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedImage(null);
                        setPreviewUrl(null);
                        if (fileInputRef.current) fileInputRef.current.value = '';
                      }}
                      className="absolute top-2 right-2 p-2 bg-black/50 rounded-full hover:bg-black/70 transition-colors"
                    >
                      <X size={16} className="text-white" />
                    </button>
                  </div>
                ) : (
                  <div
                    onClick={() => fileInputRef.current?.click()}
                    className="border-2 border-dashed border-border-light rounded-xl p-8 text-center cursor-pointer hover:border-brand-primary/50 transition-colors"
                  >
                    <Upload size={32} className="mx-auto mb-3 text-text-secondary" />
                    <p className="body-small text-text-secondary">
                      Click to select an image
                    </p>
                    <p className="caption text-text-secondary mt-1">
                      Max 5MB • PNG, JPG, WebP
                    </p>
                  </div>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileSelect}
                  className="hidden"
                  data-testid="file-input"
                />
              </div>

              {/* Uploader Name */}
              <div className="form-group">
                <label className="form-label">
                  <User size={14} className="inline mr-2" />
                  Your Name
                </label>
                <input
                  type="text"
                  value={uploaderName}
                  onChange={(e) => setUploaderName(e.target.value)}
                  className="form-input"
                  placeholder="Enter your name"
                  data-testid="uploader-name"
                  required
                />
              </div>

              {/* Caption */}
              <div className="form-group">
                <label className="form-label">
                  <MessageSquare size={14} className="inline mr-2" />
                  Caption (optional)
                </label>
                <textarea
                  value={caption}
                  onChange={(e) => setCaption(e.target.value)}
                  className="form-input min-h-[80px] resize-none"
                  placeholder="Describe your epic moment..."
                  data-testid="caption-input"
                />
              </div>

              {/* Info Notice */}
              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
                <p className="body-small text-yellow-500">
                  <strong>Note:</strong> Your screenshot will be reviewed by an admin before appearing in the gallery.
                </p>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                className="btn-primary w-full flex items-center justify-center gap-2"
                data-testid="submit-upload"
                disabled={isUploading || !selectedImage || !uploaderName}
              >
                {isUploading ? (
                  <>
                    <Loader2 className="animate-spin" size={18} />
                    Uploading...
                  </>
                ) : (
                  <>
                    <CheckCircle2 size={18} />
                    Submit Screenshot
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Lightbox */}
      {lightboxImage && (
        <div
          className="fixed inset-0 bg-black/95 flex items-center justify-center z-50 p-4"
          onClick={() => setLightboxImage(null)}
        >
          <button
            onClick={() => setLightboxImage(null)}
            className="absolute top-4 right-4 p-2 text-white/70 hover:text-white transition-colors"
          >
            <X size={32} />
          </button>
          <div className="max-w-5xl w-full" onClick={(e) => e.stopPropagation()}>
            <img
              src={lightboxImage.image_data}
              alt={lightboxImage.filename}
              className="w-full rounded-lg"
            />
            <div className="mt-4 text-center">
              <p className="text-white font-medium">{lightboxImage.uploader_name}</p>
              {lightboxImage.caption && (
                <p className="text-white/70 mt-1">{lightboxImage.caption}</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GalleryPage;
